import {EventEmitter} from "events";
import {AssetLoader, IAssetLoaderOptions} from "./AssetLoader";
import objectAssign = require('object-assign');

export interface IAssetLoaderConfig
{
    basePath?: string;
    crossOrigin?: boolean;
    blob?: boolean;
    log?: boolean;
    webAudioContext?: AudioContext;
    isTouchLocked?: boolean;
}

const DefaultConfig: IAssetLoaderConfig = {
    basePath: "",
    crossOrigin: false,
    blob: false,
    log: true,
    webAudioContext: null,
    isTouchLocked: false,
};

export interface IAsset
{
    url: string;
    file: any;
    type: string;
}


export class AssetsLoadManager extends EventEmitter
{
    static readonly EVENT_PROGRESS = "EVENT_PROGRESS";
    static readonly EVENT_COMPLETE = "EVENT_COMPLETE";
    static readonly EVENT_ASSET_COMPLETE = "EVENT_ASSET_COMPLETE";
    static readonly EVENT_ERROR = "EVENT_ERROR";
    static readonly EVENT_DESTROY = "EVENT_DESTROY";

    private _config: IAssetLoaderConfig;
    private _map: any = {};
    private _assets: any[] = [];
    private _queue: any[] = [];
    private _numLoaded: number = 0;
    private _numTotal: number = 0;
    private _loaders: any = {};

    constructor(assetsToLoad: IAssetLoaderOptions[], config?: IAssetLoaderConfig)
    {
        super();
        this.setMaxListeners(20);

        if (!config)
        {
            config = objectAssign({}, DefaultConfig);
        }
        else
        {
            config = objectAssign({}, DefaultConfig, config);
        }

        this._config = config;
        this.addMultiple(assetsToLoad);
    }

    add = (options: IAssetLoaderOptions): void =>
    {
        options = objectAssign({}, this._config, options);
        let loader: AssetLoader = new AssetLoader(options);

        loader.once(AssetLoader.EVENT_DESTROY, this.destroyHandler);
        this._queue.push(loader);
        this._loaders[loader.url] = loader;
    };

    addMultiple = (assetsToLoad: IAssetLoaderOptions[]): void =>
    {
        if (assetsToLoad)
        {
            assetsToLoad.forEach(this.add);
        }
    };

    getLoadedAsset = (url: string): IAsset =>
    {
        if (this._map[url])
        {
            return this._map[url] as IAsset;
        }
        return null;
    };

    getAssetLoader = (url: string): AssetLoader =>
    {
        if (this._loaders[url])
        {
            return this._loaders[url] as AssetLoader;
        }
        return null;
    };

    start = (): void =>
    {
        this._numTotal = this._queue.length;

        this._queue.forEach((loader: any) =>
        {
            loader
                .on(AssetLoader.EVENT_PROGRESS, this.progressHandler)
                .once(AssetLoader.EVENT_COMPLETE, this.completeHandler)
                .once(AssetLoader.EVENT_ERROR, this.errorHandler)
                .start();
        });

        this._queue = [];
    };

    progressHandler = (progress: number): void =>
    {
        const loaded: number = this._numLoaded + progress;
        //console.log(progress, this._numLoaded, this._numTotal);
        this.emit(AssetsLoadManager.EVENT_PROGRESS, loaded / this._numTotal);
    };

    completeHandler = (asset: IAsset, url: string, type: string): void =>
    {
        if (Array.isArray(asset))
        {
            asset = {url: url, file: asset, type: type};
        }
        this._numLoaded++;
        this.emit(AssetsLoadManager.EVENT_PROGRESS, this._numLoaded / this._numTotal);
        this._map[asset.url] = asset.file;
        this._assets.push(asset);
        this.emit(AssetsLoadManager.EVENT_ASSET_COMPLETE, asset);
        this.checkComplete();
    };

    errorHandler = (err: any): void =>
    {
        this._numTotal--;
        if (this.listeners(AssetsLoadManager.EVENT_ERROR).length)
        {
            this.emit(AssetsLoadManager.EVENT_ERROR, err);
        }
        else
        {
            console.error(err);
        }
        this.checkComplete();
    };

    destroyHandler = (url: string): void =>
    {
        this._loaders[url] = null;
        delete this._loaders[url];

        this._map[url] = null;
        delete this._map[url];

        this._assets.some((asset: IAsset, i: number) =>
        {
            if (asset.url === url)
            {
                this._assets.splice(i, 1);
                return true;
            }
        });
    };

    checkComplete = (): void =>
    {
        if (this._numLoaded >= this._numTotal)
        {
            this.emit(AssetsLoadManager.EVENT_COMPLETE, this._assets, this._map);
        }
    };

    destroy = (): void =>
    {
        while (this._queue.length)
        {
            this._queue.pop().destroy();
        }
        this.removeAllListeners(AssetsLoadManager.EVENT_ERROR);
        this.removeAllListeners(AssetsLoadManager.EVENT_PROGRESS);
        this.removeAllListeners(AssetsLoadManager.EVENT_COMPLETE);
        this.removeAllListeners(AssetsLoadManager.EVENT_ASSET_COMPLETE);
        this._assets = [];
        this._map = {};
        this._numTotal = 0;
        this._numLoaded = 0;

        Object.keys(this._loaders).forEach((key: string) =>
        {
            this._loaders[key].destroy();
        });
        this._loaders = {};

        this.emit(AssetsLoadManager.EVENT_DESTROY);
    };

    get numLoaded(): number
    {
        return this._numLoaded;
    }

    get numTotal(): number
    {
        return this._numTotal;
    }

    public static isSupportedFile(url:string):boolean
    {
        return AssetLoader.isSupportedFile(url);
    }
}